import { Space, Table, Tag } from "antd";
import { BellOutlined } from "@ant-design/icons";
import MotionIconButton from "../../components/icons/MotionIcon";
import { useEffect, useState } from "react";
import CompanyOnboardModal from "./CompanyOnboardModal";
import axiosInstance from "../../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import RightArrowIcon from "../../components/icons/RightArrowIcon";
import { useSelector } from "react-redux";
import { NotificationDrawer } from "./ChatDrawer";
import { useTheme } from "../../utils/themeToggle/theme";

const BoldPlusIcon = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect x="10" y="3" width="4" height="18" rx="1" />
        <rect x="3" y="10" width="18" height="4" rx="1" />
    </svg>
);

const tailwindColors = [
    "#7dd3fc", // sky-300
    "#fdba74", // orange-300
    "#5eead4", // teal-300
    "#fde68a", // yellow-300
    "#fca5a5"  // red-300
];
const routeMap = {
    "Fresh Request": "fresh_request",
    "Under Negotiation": "under_negotiation",
    "Under Execution": "under_execution",
    "Executed": "executed",
    "Transaction Closed": "transaction_closed",
};


export default function CompanyDashboard() {
    const [openModal, setOpenModal] = useState(false);
    const [activeDrawerCompanyId, setActiveDrawerCompanyId] = useState(null);
    const [allNotifications, setAllNotifications] = useState({});
    const { darkMode } = useTheme();
    const navigate = useNavigate();
    const [companyData, setCompanyData] = useState([]);
    const user_id = useSelector((store) => store.auth.userInfo.user_id);

    const STATUS_LABELS = {
        FRESH_REQUEST: "Fresh Request",
        UNDER_NEGOTIATION: "Under Negotiation",
        UNDER_EXECUTION: "Under Execution",
        EXECUTED: "Executed",
        TRANSACTION_CLOSED: "Transaction Closed",
    };

    const STATUS_COLOR_MAP = {
        "Fresh Request": "blue",
        "Under Negotiation": "pink",
        "Under Execution": "green",
        "Executed": "purple",
        "Transaction Closed": "red",
    };

    const fetchCompanyAndAgreementData = async () => {
        try {
            const companyRes = await axiosInstance.get("/company/get/");
            const companyList = companyRes.data;
            const agreementRes = await axiosInstance.post(
                "dashboard/all_companes_agreement_count/",
            );
            const agreementStats = agreementRes.data?.data || [];

            const notificationCount = await axiosInstance.post("all_company_notifications/", {
                "user_id": user_id
            });
            setAllNotifications(notificationCount.data.notifications || {});
            const chartCountMap = {};
            for (let [company_id, notifications] of Object.entries(notificationCount.data.notifications)) {
                const chat_count_sum = notifications.notifications.reduce((acc, curr) => acc + (curr.chat_count || 0), 0);
                const agreement_id = notifications.notifications?.[0]?.agreement_id;
                chartCountMap[company_id] = [chat_count_sum, agreement_id];
            }

            const transformed = companyList.map((company) => {
                const agreementData = agreementStats.find(
                    (item) => item.company_id === company.id
                );
                const agreementEntries = {};
                if (agreementData) {
                    for (let rawKey in STATUS_LABELS) {
                        const label = STATUS_LABELS[rawKey];
                        const read = agreementData.count?.[rawKey] || 0;
                        const unread = agreementData.today_count?.[rawKey] || 0;
                        agreementEntries[label] = {
                            read,
                            unread,
                            color: STATUS_COLOR_MAP[label],
                        };
                    }
                }

                return {
                    key: company.id,
                    id: company.id,
                    company: company.name,
                    agreements: agreementEntries,
                    unread: Object.values(agreementData?.today_count || {}).reduce(
                        (acc, curr) => acc + curr,
                        0
                    ),
                    chats: chartCountMap[company.id] || 0,
                    mentioned: 4,
                };
            });

            setCompanyData(transformed);
        } catch (error) {
            console.error("Error fetching company/agreement data", error);
        }
    };

    function darken(hex, amount) {
        return (
            "#" +
            hex
                .replace(/^#/, "")
                .match(/.{2}/g)
                .map((c) =>
                    Math.max(0, parseInt(c, 16) - amount)
                        .toString(16)
                        .padStart(2, "0")
                )
                .join("")
        );
    }


    const columns = [
        {
            title: 'Company',
            dataIndex: 'company',
            key: 'company',
            width: 80, // Adjust as needed
            ellipsis: true,
            render: (text) => (
                <div style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '12px',
                    fontWeight: 'bold',

                }}>
                    {text}
                </div>
            )
        },

        {
            title: "Agreement Status",
            width: 400, // in pixels
            ellipsis: true,
            dataIndex: "agreements",
            key: "agreements",
            render: (agreements, record) => {
                if (!agreements || typeof agreements !== "object") return null;

                const statusList = Object.entries(agreements);

                return (
                    <div className="grid grid-cols-5 gap-2 text-xs text-center font-medium">
                        {statusList.map(([status, { read, unread }], idx) => {
                            const color = tailwindColors[idx % tailwindColors.length];
                            const textColor = darken(color, 40);
                            const style = {
                                backgroundColor: `${color}33`,
                                border: `1.5px solid ${color}`,
                                color: textColor,
                                borderRadius: "6px",
                                padding: "3px 8px",
                                fontSize: "12px",
                                fontWeight: 600,
                                display: "inline-block",
                                marginTop: "4px",
                                cursor: "pointer",
                            };

                            const statusKey = routeMap[status];

                            return (
                                <div
                                    key={status}
                                    className="flex flex-col gap-1 items-center justify-center"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        localStorage.setItem("companyId", record.id);
                                        navigate(`/admin/contracts/agreement_type/${statusKey}/`);
                                    }}
                                >
                                    <div className="flex gap-2">
                                        <p style={{ color: darkMode ? textColor : "#374151" }}>
                                            Unread: <strong className="text-[13px]">{unread}</strong>,
                                        </p>
                                        <p style={{ color: darkMode ? textColor : "#374151" }}>
                                            Total: <strong className="text-[13px]">{read}</strong>
                                        </p>
                                    </div>
                                    <span style={style}>{status}</span>
                                </div>
                            );
                        })}
                    </div>
                );
            },
        },
        {
            title: "Chats",
            width: 40, // in pixels
            ellipsis: true,
            dataIndex: "chats",
            key: "chats",
            render: (val, record) => (
                <button
                    onClick={(e) => {
                        e.stopPropagation(); // prevent row click
                        setActiveDrawerCompanyId(
                            activeDrawerCompanyId === record.id ? null : record.id
                        );
                    }}
                    className={`relative rounded-md text-sm font-medium flex items-center gap-2 cursor-pointer
                        }`}
                >
                    <div className="absolute top-[-7px] p-2 left-4 bg-blue-600 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {val[0]}
                    </div>
                    <BellOutlined className="text-blue-600 text-3xl" />
                </button>
            ),
        },

        {
            title: "Action",
            width: 40, // in pixels
            ellipsis: true,
            key: "action",
            render: (_, record) => (
                <button
                    // onClick={() => handleViewCompany(record.id)} // 👈 ID dynamic pass
                    className=" px-3 py-1 rounded-md"
                >
                    <RightArrowIcon />
                </button>
            ),
        },

    ];
    const handleViewCompany = (record, navigateTo = `/admin/company/${record.id}/dashboard`) => {
        localStorage.setItem("companyId", record.id); // 👈 Store ID in localStorage if needed
        navigate(navigateTo); // 👈 Replace with dynamic ID
    }

    useEffect(() => {
        fetchCompanyAndAgreementData()
    }, [])

    return (
        <div className="">
            <div className="mb-6 flex flex-row justify-between ">
                <Space>
                    <span>
                        <h1
                            className="mt-1 text-2xl font-bold dark:text-white"            >
                            Client Dashboard
                        </h1>
                    </span>
                </Space>
                <MotionIconButton onClick={() => setOpenModal(true)} tooltip="OnBoard New Client" icon={<BoldPlusIcon />} />
            </div>
            <div className="rounded-2xl shadow-md overflow-x-auto">
                <Table
                    columns={columns}
                    dataSource={companyData}
                    pagination={false}
                    bordered
                    className="text-[12px] [&_.ant-table-cell]:p-1 [&_.ant-table-cell]:text-[12px]"
                    scroll={{ x: 1200 }}
                    onRow={(record) => ({
                        onClick: () => handleViewCompany(record),
                        style: { cursor: "pointer" },
                    })}
                />
            </div>
            <CompanyOnboardModal
                open={openModal}
                onClose={() => { setOpenModal(false); fetchCompanyAndAgreementData() }}
                userType="professional"

            />
            <NotificationDrawer
                companyId={activeDrawerCompanyId}
                onClose={() => setActiveDrawerCompanyId(null)}
                allNotifications={allNotifications}
                fetchNotifications={fetchCompanyAndAgreementData}
                user_id={user_id}
            />

        </div>
    );
}

